import React from "react";
import * as Modal from "../../../../../common-deprecated/styles/v2/lexus/globals/Modal";
import * as Type from "../../../../../common-deprecated/styles/v2/lexus/globals/Type";
import * as ButtonGroup from "../../../../../common-deprecated/styles/v2/lexus/components/ButtonGroup";
import Icon from "../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { IconWrapper } from "../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import { type CompareV2DispatchType, useCompareV2Selector } from "../../../redux/store";
import { compareAddCar } from "../../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import useMaxNumberToCompare from "../../../../../shared-logic/features/compare/hooks/useMaxNumberToCompare";
import { getNameFromModelGradeBodyTypeIdsAndAvailabelCars } from "../../../utils/formatters/nameFormatters";
import { useCompareV2Label } from "../../../utils/labels";
import type { ReplaceCarType } from "../../../../../shared-logic/features/compare/utils/modalConstants";
import type { ModalViewType } from "../../../../../common-deprecated/types/CommonTypes";
import ModalBaseView from "../../../../../common-deprecated/components/modals/aem/ModalBaseView";
import ButtonPrimary from "../../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import ButtonTertiary from "../../../../../common-deprecated/components/aem/lexus/button/ButtonTertiary";
import { useDispatch } from "../../../../../shared-logic/redux/store";

/**
 * Both MultipleChoice and ModalBaseView were copied over to here as they are no longer used anywhere else.
 * We also no longer use this pattern for new modals.
 */

type MultipleChoiceType = {
    list: {
        id: string;
        label?: string;
        handler?: (index: number) => void;
    }[];
    onCancel: () => void;
    cancelLabel: string;
};

const MultipleChoice = (props: MultipleChoiceType): JSX.Element => {
    const { list, onCancel, cancelLabel } = props;
    return (
        <Modal.Actions>
            <ButtonGroup.Vertical>
                {list.map((actionItem, index) => {
                    return (
                        <ButtonPrimary
                            fullWidth
                            key={actionItem.id}
                            onClick={() => actionItem.handler && actionItem.handler(index)}
                        >
                            {actionItem.label || ""}
                        </ButtonPrimary>
                    );
                })}
                <ButtonTertiary fullWidth onClick={onCancel}>
                    {cancelLabel}
                </ButtonTertiary>
            </ButtonGroup.Vertical>
        </Modal.Actions>
    );
};

const LexusReplaceCarModal = (props: ModalViewType<ReplaceCarType>): JSX.Element => {
    const { show, close, modalSettings } = props;
    const { newCarToAdd } = modalSettings;

    // TODO: find a better way to handle reducers not being defined in newCars components
    // optionally chaining here because these modals are used for both used and new cars, but usedCarReducer is not initialized in the Build & Grade explorer
    const newCars = useCompareV2Selector((state) => state.newCarCompare?.grades || []);
    const allNewCars = useCompareV2Selector((state) => state.newCarCompare?.available || []);

    const [loadingLabel, compareModalCancelButton] = useCompareV2Label([
        "compareBodyLoading",
        "compareModalCancelButton",
    ]);

    const dispatch = useDispatch<CompareV2DispatchType>();
    const maxNumberToCompare = useMaxNumberToCompare();

    // We are injecting this label as HTML because there are 2 value fields in this single label
    let carToAddHTML = "";
    let options: MultipleChoiceType["list"] = [];
    if (newCarToAdd) {
        carToAddHTML = `<strong>${getNameFromModelGradeBodyTypeIdsAndAvailabelCars(newCarToAdd, allNewCars)}</strong>`;
        options = newCars.map((car, index) => ({
            id: `${car.id}-${index}`,
            label: car.modelName
                ? [car.modelName, car.gradeName, car.bodyTypeName].filter(Boolean).join(" ")
                : loadingLabel,
            handler: (replaceIndex: number) => {
                dispatch(compareAddCar(newCarToAdd, null, replaceIndex));
                close();
            },
        }));
    }
    const label = useCompareV2Label("compareModalReplaceCar", {
        maxNumberToCompare: `<strong>${maxNumberToCompare}</strong>`,
        newCar: carToAddHTML,
    });

    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="sm">
            <Modal.Header>
                <Modal.Button type="button" onClick={close}>
                    <IconWrapper>
                        <Icon variant="close" />
                    </IconWrapper>
                </Modal.Button>
            </Modal.Header>
            <Modal.Body>
                <Type.Text dangerouslySetInnerHTML={{ __html: label }} />
            </Modal.Body>
            <MultipleChoice list={options} onCancel={close} cancelLabel={compareModalCancelButton} />
        </ModalBaseView>
    );
};

export default LexusReplaceCarModal;
